import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, Archive, Calendar, CalendarPlus, CheckCircle2, ChevronRight, Clock,
  ExternalLink, Loader2, MoreHorizontal, Plus, Search, Square, Zap,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { frDate } from "@/lib/constants";

/* ---------------- Référentiels ---------------- */
const SERVICES = [
  { id: "Community Management", cls: "bg-emerald-50 text-emerald-600" },
  { id: "Graphisme", cls: "bg-amber-50 text-amber-600" },
  { id: "Web Design", cls: "bg-sky-50 text-sky-600" },
  { id: "Comptabilité", cls: "bg-violet-50 text-violet-600" },
  { id: "Prospection", cls: "bg-rose-50 text-rose-600" },
  { id: "Commercial", cls: "bg-orange-50 text-orange-600" },
  { id: "Stratégie", cls: "bg-indigo-50 text-indigo-600" },
  { id: "Événement", cls: "bg-amber-100 text-amber-700" },
  { id: "Administration", cls: "bg-neutral-100 text-neutral-500" },
  { id: "autre", cls: "bg-neutral-100 text-neutral-500" },
];
const serviceCls = (s) => (SERVICES.find((x) => x.id === s) || SERVICES[9]).cls;

const PRIORITIES = [
  { id: "haute", label: "Haute", cls: "bg-rose-50 text-rose-500" },
  { id: "moyenne", label: "Moyenne", cls: "bg-amber-50 text-amber-600" },
  { id: "normale", label: "Moyenne", cls: "bg-amber-50 text-amber-600" },
  { id: "basse", label: "Basse", cls: "bg-emerald-50 text-emerald-600" },
];
const prioOf = (p) => PRIORITIES.find((x) => x.id === p) || PRIORITIES[1];

const SOURCE_TYPES = [
  { id: "", label: "Aucun" },
  { id: "publication", label: "Publication", endpoint: "/publications", labelOf: (x) => x.title, urlOf: (x) => `/publications/${x.id}` },
  { id: "quote", label: "Devis", endpoint: "/accounting/quotes", labelOf: (x) => `Devis ${x.number || ""} — ${x.client_name || ""}`, urlOf: (x) => `/comptabilite?tab=devis&quote=${x.id}` },
  { id: "invoice", label: "Facture", endpoint: "/accounting/invoices", labelOf: (x) => `Facture ${x.number || ""} — ${x.client_name || ""}`, urlOf: (x) => `/comptabilite?tab=factures&invoice=${x.id}` },
  { id: "client_request", label: "Demande client", endpoint: "/client-requests", labelOf: (x) => x.title || x.type, urlOf: () => "/demandes" },
  { id: "graphic_project", label: "Projet graphique", endpoint: "/graphisme/projects", labelOf: (x) => x.name || x.title, urlOf: (x) => `/graphisme/projet/${x.id}` },
  { id: "web_project", label: "Projet web", endpoint: "/web/projects", labelOf: (x) => x.name || x.title, urlOf: (x) => `/sites-web/projet/${x.id}` },
  { id: "prospect", label: "Prospect", endpoint: "/prospects", labelOf: (x) => x.company || x.name, urlOf: () => "/prospection" },
  { id: "appointment", label: "Rendez-vous", endpoint: "/appointments", labelOf: (x) => `${x.title || "RDV"} — ${x.date || ""}`, urlOf: () => "/rdv" },
];
const sourceTypeOf = (id) => SOURCE_TYPES.find((s) => s.id === id);

/* ---------------- Helpers ---------------- */
const initials = (n = "") => n.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "·";
const fmtMin = (min) => { const h = Math.floor((min || 0) / 60); const m = (min || 0) % 60; return h > 0 ? `${h} h${m ? ` ${String(m).padStart(2, "0")}` : ""}` : `${m} min`; };
const fmtSec = (s) => `${Math.floor(s / 3600)} h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const tomorrowISO = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };

function deadlineChip(t) {
  const dl = t.deadline || t.next_slot?.date;
  if (!dl) return { label: "Aucune date", cls: "text-neutral-400", icon: Calendar };
  const today = todayISO();
  if (dl < today) return { label: `En retard · ${frDate(dl)}`, cls: "text-rose-500 font-semibold", icon: AlertTriangle };
  if (dl === today) return { label: "Aujourd'hui", cls: "text-rose-500 font-semibold", icon: Calendar };
  if (dl === tomorrowISO()) return { label: "Demain", cls: "text-amber-600", icon: Calendar };
  return { label: frDate(dl), cls: "text-neutral-500", icon: Calendar };
}

const isDone = (t) => t.status === "finalise" || t.status === "termine" || t.archived;
const urgentOf = (t) => {
  if (isDone(t)) return false;
  const lim = tomorrowISO();
  if (t.deadline && t.deadline <= lim) return true;
  if (t.next_slot && t.next_slot.date <= lim) return true;
  return false;
};
const columnOf = (t) => {
  if (urgentOf(t)) return "urgent";
  if (t.status === "programme") return "programme";
  if (["en_cours", "envoye_validation", "livre_client"].includes(t.status)) return "en_cours";
  return "a_faire";
};

/* ---------------- KPI ---------------- */
function Kpi({ testid, icon: Icon, bg, fg, label, value, sub }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl shadow-sm p-2.5 flex items-center gap-2 min-w-0" data-testid={testid}>
      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: bg }}>
        <Icon className="w-4 h-4" style={{ color: fg }} />
      </span>
      <div className="min-w-0">
        <p className="text-[9px] font-medium text-neutral-500 leading-tight truncate">{label}</p>
        <p className="font-heading text-lg font-bold text-[#0F172A] leading-tight" data-testid={`${testid}-value`}>{value}</p>
        {sub && <p className="text-[8px] text-neutral-400 truncate">{sub}</p>}
      </div>
    </div>
  );
}

/* ---------------- Carte tâche ---------------- */
function TaskCard({ task, clientName, clientColor, onOpen, onMenu, menuId }) {
  const pr = prioOf(task.priority);
  const dl = deadlineChip(task);
  const spent = task.time_spent_sec || 0;
  const est = (task.estimate_min || 0) * 60;
  const pct = est > 0 ? Math.min(100, Math.round(spent / est * 100)) : null;
  return (
    <div draggable onDragStart={(e) => e.dataTransfer.setData("text/task-id", task.id)}
      onClick={() => onOpen(task)} data-testid={`todo-task-${task.id}`}
      className="bg-white border border-[#E2E8F0] rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer group relative">
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full text-[8px] font-bold flex items-center justify-center shrink-0"
          style={{ background: `${clientColor}1F`, color: clientColor }}>{initials(clientName)}</span>
        <span className="text-[11px] font-semibold text-[#0F172A] truncate flex-1">{clientName || "Interne"}</span>
        <button onClick={(e) => { e.stopPropagation(); onMenu(task); }} data-testid={`todo-task-menu-${task.id}`}
          className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-neutral-100 text-neutral-400 transition-opacity">
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-[12px] font-medium text-[#334155] mt-1 leading-snug line-clamp-2">{task.title}</p>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {task.service && <span className={`text-[9px] font-semibold rounded-full px-2 py-0.5 ${serviceCls(task.service)}`}>{task.service === "Community Management" ? "Community" : task.service}</span>}
        <span className={`text-[9px] font-semibold rounded-full px-2 py-0.5 ${pr.cls}`}>{pr.label}</span>
      </div>
      {columnOf(task) === "en_cours" && pct != null && (
        <div className="flex items-center gap-1.5 mt-2" data-testid={`todo-task-progress-${task.id}`}>
          <span className="text-[10px] font-semibold text-[#1D4ED8] w-7">{pct} %</span>
          <span className="flex-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
            <span className="block h-full bg-[#3B82F6] rounded-full" style={{ width: `${pct}%` }} />
          </span>
        </div>
      )}
      <div className="flex items-center gap-2.5 mt-2 text-[10px] text-neutral-400">
        <span className={`inline-flex items-center gap-1 ${dl.cls}`}><dl.icon className="w-3 h-3" /> {dl.label}</span>
        {task.estimate_min ? <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtMin(task.estimate_min)}</span> : null}
        {task.timer_started_at && <span className="inline-flex items-center gap-1 text-rose-500 font-semibold"><Square className="w-2.5 h-2.5 fill-current" /> chrono</span>}
        {task.assignee && (
          <span className="ml-auto w-5 h-5 rounded-full bg-neutral-800 text-white text-[8px] font-bold flex items-center justify-center" title={task.assignee}>
            {initials(task.assignee)}
          </span>
        )}
      </div>
    </div>
  );
}

/* ---------------- Modale création / édition ---------------- */
function TaskModal({ open, onClose, onSaved, clients, users, task }) {
  const empty = { title: "", description: "", client_id: "", service: "", assignee: "", priority: "moyenne", deadline: "", estimate_min: "", source_type: "", source_id: "" };
  const [form, setForm] = useState(empty);
  const [sched, setSched] = useState({ date: "", time: "", duration: 60 });
  const [entities, setEntities] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(task ? {
      title: task.title, description: task.description || "", client_id: task.client_id || "",
      service: task.service || "", assignee: task.assignee || "", priority: task.priority || "moyenne",
      deadline: task.deadline || "", estimate_min: task.estimate_min || "",
      source_type: task.source_type || "", source_id: task.source_id || "",
    } : empty);
    setSched({ date: "", time: "", duration: task?.estimate_min || 60 });
  }, [open, task]);

  useEffect(() => {
    const st = sourceTypeOf(form.source_type);
    if (!st || !st.endpoint) { setEntities([]); return; }
    api.get(st.endpoint).then((r) => setEntities(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => setEntities([]));
  }, [form.source_type]);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Le titre est requis"); return; }
    setSaving(true);
    try {
      const st = sourceTypeOf(form.source_type);
      const entity = entities.find((e) => e.id === form.source_id);
      const payload = {
        ...form,
        client_id: form.client_id || null, service: form.service || null,
        assignee: form.assignee || null, deadline: form.deadline || null,
        estimate_min: form.estimate_min ? parseInt(form.estimate_min, 10) : null,
        source_type: st?.id || null, source_id: entity?.id || null,
        source_label: entity ? st.labelOf(entity) : null,
        source_url: entity ? st.urlOf(entity) : null,
      };
      let saved;
      if (task) {
        saved = (await api.patch(`/tasks/${task.id}`, payload)).data;
      } else {
        saved = (await api.post("/tasks", payload)).data;
      }
      // Programmation → bloc agenda lié (synchro bidirectionnelle existante)
      if (!task && sched.date && sched.time) {
        const [h, m] = sched.time.split(":").map(Number);
        try {
          await api.post("/agenda/blocks", {
            task_id: saved.id, title: saved.title, client_id: saved.client_id,
            date: sched.date, start_min: h * 60 + m, end_min: h * 60 + m + (parseInt(sched.duration, 10) || 60),
            type: "internal_task",
          });
          toast.success("Tâche créée et programmée dans l'Agenda");
        } catch { toast.success("Tâche créée (programmation agenda impossible)"); }
      } else {
        toast.success(task ? "Tâche mise à jour" : "Tâche créée");
      }
      onSaved();
      onClose();
    } catch { toast.error("Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  const inputCls = "h-9 text-xs rounded-lg border border-neutral-200 px-2.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#1D4ED8] w-full";
  const selCls = inputCls + " cursor-pointer";
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="task-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading">{task ? "Modifier la tâche" : "Ajouter une tâche"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold text-neutral-500">Titre *</label>
            <input className={inputCls} data-testid="task-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Modifier la publication Bague Jaya" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-neutral-500">Description</label>
            <textarea className={inputCls + " h-16 py-2 resize-none"} data-testid="task-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-neutral-500">Client</label>
              <select className={selCls} data-testid="task-form-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                <option value="">Interne / aucun</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-neutral-500">Service</label>
              <select className={selCls} data-testid="task-form-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
                <option value="">—</option>
                {SERVICES.filter((s) => s.id !== "autre").map((s) => <option key={s.id} value={s.id}>{s.id}</option>)}
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-neutral-500">Responsable</label>
              <select className={selCls} data-testid="task-form-assignee" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })}>
                <option value="">—</option>
                {users.map((u) => <option key={u.id || u.name} value={u.name}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-neutral-500">Priorité</label>
              <select className={selCls} data-testid="task-form-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value="basse">Basse</option>
                <option value="moyenne">Moyenne</option>
                <option value="haute">Haute</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-neutral-500">Deadline</label>
              <input type="date" className={inputCls} data-testid="task-form-deadline" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-neutral-500">Durée estimée (min)</label>
              <input type="number" min="0" step="15" className={inputCls} data-testid="task-form-estimate" value={form.estimate_min} onChange={(e) => setForm({ ...form, estimate_min: e.target.value })} placeholder="120" />
            </div>
          </div>
          {!task && (
            <div className="border border-neutral-100 rounded-xl p-3 bg-neutral-50/60">
              <p className="text-[11px] font-semibold text-neutral-500 flex items-center gap-1.5"><CalendarPlus className="w-3.5 h-3.5" /> Programmer (apparaît dans l'Agenda)</p>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <input type="date" className={inputCls} data-testid="task-form-sched-date" value={sched.date} onChange={(e) => setSched({ ...sched, date: e.target.value })} />
                <input type="time" className={inputCls} data-testid="task-form-sched-time" value={sched.time} onChange={(e) => setSched({ ...sched, time: e.target.value })} />
                <select className={selCls} data-testid="task-form-sched-duration" value={sched.duration} onChange={(e) => setSched({ ...sched, duration: e.target.value })}>
                  {[30, 60, 90, 120, 180, 240].map((m) => <option key={m} value={m}>{fmtMin(m)}</option>)}
                </select>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-neutral-500">Élément lié</label>
              <select className={selCls} data-testid="task-form-source-type" value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value, source_id: "" })}>
                {SOURCE_TYPES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-neutral-500">&nbsp;</label>
              <select className={selCls} data-testid="task-form-source-id" value={form.source_id} onChange={(e) => setForm({ ...form, source_id: e.target.value })} disabled={!form.source_type}>
                <option value="">—</option>
                {entities.slice(0, 100).map((e) => <option key={e.id} value={e.id}>{(sourceTypeOf(form.source_type)?.labelOf(e) || e.id).slice(0, 60)}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} data-testid="task-form-cancel" className="px-3.5 py-2 text-xs font-semibold text-neutral-500 hover:bg-neutral-100 rounded-lg transition-colors">Annuler</button>
            <button onClick={save} disabled={saving} data-testid="task-form-submit"
              className="inline-flex items-center gap-1.5 bg-[#0B132B] text-white rounded-lg px-4 py-2 text-xs font-semibold hover:bg-[#1C2A4F] transition-colors disabled:opacity-50">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {task ? "Enregistrer" : "Créer la tâche"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Modale de programmation (drag vers Programmée sans date) ---------------- */
function ScheduleModal({ task, onClose, onSaved }) {
  const [date, setDate] = useState(tomorrowISO());
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState(task?.estimate_min || 60);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (task) setDuration(task.estimate_min || 60); }, [task]);
  if (!task) return null;
  const save = async () => {
    setSaving(true);
    try {
      const [h, m] = time.split(":").map(Number);
      await api.post("/agenda/blocks", {
        task_id: task.id, title: task.title, client_id: task.client_id,
        date, start_min: h * 60 + m, end_min: h * 60 + m + (parseInt(duration, 10) || 60),
        type: "internal_task",
      });
      toast.success("Tâche programmée dans l'Agenda");
      onSaved();
      onClose();
    } catch { toast.error("Programmation impossible"); }
    finally { setSaving(false); }
  };
  const inputCls = "h-9 text-xs rounded-lg border border-neutral-200 px-2.5 bg-white w-full";
  return (
    <Dialog open={!!task} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="task-schedule-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-base">Programmer « {task.title} »</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          <input type="date" className={inputCls} data-testid="schedule-date" value={date} onChange={(e) => setDate(e.target.value)} />
          <input type="time" className={inputCls} data-testid="schedule-time" value={time} onChange={(e) => setTime(e.target.value)} />
          <select className={inputCls} data-testid="schedule-duration" value={duration} onChange={(e) => setDuration(e.target.value)}>
            {[30, 60, 90, 120, 180, 240].map((m) => <option key={m} value={m}>{fmtMin(m)}</option>)}
          </select>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose} className="px-3.5 py-2 text-xs font-semibold text-neutral-500 hover:bg-neutral-100 rounded-lg">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="schedule-submit"
            className="bg-[#0B132B] text-white rounded-lg px-4 py-2 text-xs font-semibold hover:bg-[#1C2A4F] disabled:opacity-50">
            {saving ? "…" : "Programmer"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Page ---------------- */
export default function Tasks() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fClient, setFClient] = useState(searchParams.get("client") || "");
  const [fService, setFService] = useState("");
  const [fAssignee, setFAssignee] = useState("");
  const [fPeriod, setFPeriod] = useState("");
  const [modal, setModal] = useState({ open: false, task: null });
  const [schedTask, setSchedTask] = useState(null);
  const [menu, setMenu] = useState(null); // {task, x, y}
  const [showDone, setShowDone] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/tasks").then((r) => setTasks(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);
  // Rafraîchissement quand une tâche est modifiée ailleurs (fiche client, dashboard…)
  useEffect(() => {
    const h = () => load();
    window.addEventListener("sf:tasks", h);
    return () => window.removeEventListener("sf:tasks", h);
  }, []);

  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

  const filtered = useMemo(() => tasks.filter((t) => {
    if (fClient && t.client_id !== fClient) return false;
    if (fService && (t.service || "autre") !== fService) return false;
    if (fAssignee && t.assignee !== fAssignee) return false;
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      if (![t.title, t.description, t.source_label, clientById[t.client_id]?.name].filter(Boolean).join(" ").toLowerCase().includes(n)) return false;
    }
    if (fPeriod === "semaine" || fPeriod === "mois") {
      const dl = t.deadline || t.next_slot?.date;
      if (!dl) return false;
      const end = new Date(); end.setDate(end.getDate() + (fPeriod === "semaine" ? 7 : 31));
      if (dl > end.toISOString().slice(0, 10)) return false;
    }
    if (fPeriod === "retard" && !(t.deadline && t.deadline < todayISO() && !isDone(t))) return false;
    return true;
  }), [tasks, fClient, fService, fAssignee, fPeriod, q, clientById]);

  const active = filtered.filter((t) => !isDone(t));
  const done = filtered.filter((t) => isDone(t));
  const cols = {
    urgent: active.filter((t) => columnOf(t) === "urgent"),
    a_faire: active.filter((t) => columnOf(t) === "a_faire"),
    en_cours: active.filter((t) => columnOf(t) === "en_cours"),
    programme: active.filter((t) => columnOf(t) === "programme"),
  };
  const weekEstimate = active.reduce((s, t) => {
    const dl = t.deadline || t.next_slot?.date;
    const end = new Date(); end.setDate(end.getDate() + 7);
    return s + (dl && dl <= end.toISOString().slice(0, 10) ? (t.estimate_min || 0) : 0);
  }, 0);
  const todayTasks = active.filter((t) => t.deadline === todayISO() || t.next_slot?.date === todayISO());

  const patch = async (t, update, msg) => {
    try {
      await api.patch(`/tasks/${t.id}`, update);
      if (msg) toast.success(msg);
      window.dispatchEvent(new Event("sf:tasks"));
      load();
    } catch { toast.error("Action impossible"); }
  };
  const toggleTimer = (t) => {
    if (t.timer_started_at) {
      const add = Math.max(1, Math.floor((Date.now() - new Date(t.timer_started_at).getTime()) / 1000));
      patch(t, { timer_started_at: null, time_spent_sec: (t.time_spent_sec || 0) + add }, "Chrono arrêté — temps ajouté au client");
    } else {
      patch(t, { timer_started_at: new Date().toISOString() }, "Chrono démarré");
    }
  };

  const onDrop = (col) => async (e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/task-id");
    const t = tasks.find((x) => x.id === id);
    if (!t || columnOf(t) === col) return;
    if (col === "programme" && !t.next_slot) { setSchedTask(t); return; } // pas de date → sélecteur automatique
    const statusMap = { a_faire: "a_faire", en_cours: "en_cours", programme: "programme" };
    if (statusMap[col]) await patch(t, { status: statusMap[col] }, col === "en_cours" ? "Tâche en cours" : null);
  };

  const menuAction = (action) => {
    const t = menu?.task;
    setMenu(null);
    if (!t) return;
    if (action === "open") setModal({ open: true, task: t });
    if (action === "schedule") setSchedTask(t);
    if (action === "timer") toggleTimer(t);
    if (action === "source" && t.source_url) navigate(t.source_url);
    if (action === "done") patch(t, { status: "finalise" }, "Tâche finalisée — dossier « Finalisées »");
    if (action === "delete") api.delete(`/tasks/${t.id}`).then(() => { toast.success("Tâche supprimée"); load(); });
  };

  const COLUMNS = [
    { id: "urgent", label: "Urgent", icon: AlertTriangle, fg: "#E11D48", bg: "#FFF1F2", hint: "Échéance proche ou dépassée (calculé automatiquement)." },
    { id: "a_faire", label: "To-do", icon: Zap, fg: "#B45309", bg: "#FFFBEB", hint: "Tâches sans échéance définie pour le moment." },
    { id: "en_cours", label: "En cours", icon: CheckCircle2, fg: "#1D4ED8", bg: "#EFF6FF", hint: "Tâches actuellement en traitement." },
    { id: "programme", label: "Programmée", icon: Calendar, fg: "#6D28D9", bg: "#F5F3FF", hint: "Tâches planifiées pour une date ultérieure (visibles dans l'Agenda)." },
  ];

  const selCls = "h-9 text-xs rounded-lg border border-neutral-200 px-2.5 bg-white focus:outline-none cursor-pointer";

  return (
    <div className="flex flex-col gap-3 h-[calc(100vh-7rem)] min-h-0" data-testid="todo-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 shrink-0">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]" data-testid="todo-title">To-do</h1>
          <p className="text-neutral-500 text-xs mt-1">Centralisez toutes les tâches de l'agence et suivez leur avancement simplement.</p>
        </div>
        <p className="text-[#64748B] italic text-sm leading-snug text-right hidden md:block">« Des équipes organisées,<br />des projets qui avancent plus loin. »</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-5 gap-2 shrink-0" data-testid="todo-kpis">
        <Kpi testid="todo-kpi-urgent" icon={AlertTriangle} bg="#FFF1F2" fg="#E11D48" label="Tâches urgentes" value={cols.urgent.length} sub="échéance ≤ demain" />
        <Kpi testid="todo-kpi-afaire" icon={Zap} bg="#FFFBEB" fg="#B45309" label="Tâches à faire" value={cols.a_faire.length} sub="sans date précise" />
        <Kpi testid="todo-kpi-encours" icon={CheckCircle2} bg="#EFF6FF" fg="#1D4ED8" label="Tâches en cours" value={cols.en_cours.length} sub="en traitement" />
        <Kpi testid="todo-kpi-programme" icon={Calendar} bg="#F5F3FF" fg="#6D28D9" label="Tâches programmées" value={cols.programme.length} sub="dans l'Agenda" />
        <Kpi testid="todo-kpi-temps" icon={Clock} bg="#ECFDF5" fg="#047857" label="Temps estimé semaine" value={fmtMin(weekEstimate)} sub="7 prochains jours" />
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 shrink-0 flex-wrap" data-testid="todo-filters">
        <div className="relative flex-1 min-w-44">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input className="pl-8 h-9 w-full text-xs rounded-lg border border-neutral-200 bg-white focus:outline-none focus:ring-1 focus:ring-[#1D4ED8]"
            placeholder="Rechercher une tâche…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="todo-search" />
        </div>
        <select className={selCls} value={fClient} onChange={(e) => { setFClient(e.target.value); setSearchParams((p) => { const n = new URLSearchParams(p); if (e.target.value) n.set("client", e.target.value); else n.delete("client"); return n; }, { replace: true }); }} data-testid="todo-filter-client">
          <option value="">Tous les clients</option>
          {clients.filter((c) => !c.archived).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className={selCls} value={fService} onChange={(e) => setFService(e.target.value)} data-testid="todo-filter-service">
          <option value="">Tous les services</option>
          {SERVICES.map((s) => <option key={s.id} value={s.id}>{s.id}</option>)}
        </select>
        <select className={selCls} value={fAssignee} onChange={(e) => setFAssignee(e.target.value)} data-testid="todo-filter-assignee">
          <option value="">Tous les collaborateurs</option>
          {users.map((u) => <option key={u.id || u.name} value={u.name}>{u.name}</option>)}
        </select>
        <select className={selCls} value={fPeriod} onChange={(e) => setFPeriod(e.target.value)} data-testid="todo-filter-period">
          <option value="">Toutes périodes</option>
          <option value="semaine">Cette semaine</option>
          <option value="mois">Ce mois</option>
          <option value="retard">En retard</option>
        </select>
        <button onClick={() => setModal({ open: true, task: null })} data-testid="todo-add-btn"
          className="ml-auto inline-flex items-center gap-1.5 bg-[#0B132B] text-white rounded-lg px-4 py-2 text-xs font-semibold hover:bg-[#1C2A4F] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Ajouter une tâche
        </button>
      </div>

      {/* Kanban + panneau droit */}
      <div className="flex-1 min-h-0 flex gap-3">
        <div className="flex-1 min-w-0 grid grid-cols-4 gap-2 min-h-0">
          {COLUMNS.map((col) => (
            <div key={col.id} className="rounded-xl border border-[#E2E8F0] min-h-0 flex flex-col min-w-0" style={{ background: col.bg }}
              onDragOver={(e) => e.preventDefault()} onDrop={onDrop(col.id)} data-testid={`todo-col-${col.id}`}>
              <div className="px-2.5 pt-2.5 pb-1.5 shrink-0">
                <div className="flex items-center gap-1.5">
                  <col.icon className="w-3.5 h-3.5 shrink-0" style={{ color: col.fg }} />
                  <h3 className="font-heading font-bold text-[12px] text-[#0F172A] truncate">{col.label}</h3>
                  <span className="text-[10px] font-bold text-neutral-500 bg-white/70 rounded-full px-1.5 py-0.5 shrink-0" data-testid={`todo-col-${col.id}-count`}>{cols[col.id].length}</span>
                </div>
                <p className="text-[8px] text-neutral-400 mt-0.5 leading-snug line-clamp-2">{col.hint}</p>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2 space-y-2" data-testid={`todo-col-${col.id}-list`}>
                {cols[col.id].length === 0 && <p className="text-[11px] text-neutral-400 text-center py-6">Aucune tâche</p>}
                {cols[col.id].map((t) => (
                  <TaskCard key={t.id} task={t} clientName={clientById[t.client_id]?.name} clientColor={clientById[t.client_id]?.color || "#0B132B"}
                    onOpen={(task) => setModal({ open: true, task })} onMenu={(task) => setMenu({ task })} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Panneau droit */}
        <div className="hidden xl:flex w-64 shrink-0 flex-col gap-3 min-h-0 overflow-y-auto pr-0.5" data-testid="todo-side">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-[#B45309]" />
              <h3 className="font-heading font-bold text-[13px]">Aujourd'hui</h3>
              <span className="text-[10px] text-neutral-400 bg-neutral-100 rounded-full px-1.5 py-0.5">{todayTasks.length}</span>
            </div>
            {todayTasks.length === 0 && <p className="text-[11px] text-neutral-400">Rien d'échu aujourd'hui.</p>}
            {todayTasks.slice(0, 4).map((t) => (
              <button key={t.id} onClick={() => setModal({ open: true, task: t })} data-testid={`todo-today-${t.id}`}
                className="w-full flex items-center gap-2 py-1.5 border-b border-neutral-50 last:border-0 text-left hover:bg-neutral-50 rounded-lg px-1 transition-colors">
                <span className="w-6 h-6 rounded-full text-[8px] font-bold flex items-center justify-center shrink-0"
                  style={{ background: `${(clientById[t.client_id]?.color || "#0B132B")}1F`, color: clientById[t.client_id]?.color || "#0B132B" }}>{initials(clientById[t.client_id]?.name)}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[11px] font-medium text-[#0F172A] truncate">{t.title}</span>
                  <span className="block text-[9px] text-neutral-400 truncate">{clientById[t.client_id]?.name || "Interne"}</span>
                </span>
                <span className={`text-[9px] font-semibold rounded-full px-1.5 py-0.5 ${prioOf(t.priority).cls}`}>{prioOf(t.priority).label}</span>
              </button>
            ))}
          </div>
          <button onClick={() => setShowDone(true)} data-testid="todo-done-folder"
            className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5 flex items-center gap-3 hover:shadow-md transition-shadow text-left">
            <span className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0"><Archive className="w-4.5 h-4.5 w-[18px] h-[18px]" /></span>
            <span className="flex-1">
              <span className="block font-heading font-bold text-[13px] text-[#0F172A]">Dossier finalisées</span>
              <span className="block text-[10px] text-neutral-400">{done.length} tâche(s) — archivées et consultables ici.</span>
            </span>
            <ChevronRight className="w-4 h-4 text-neutral-300" />
          </button>
        </div>
      </div>

      {/* Menu contextuel carte */}
      {menu && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setMenu(null)} />
          <div className="fixed z-40 right-6 bottom-6 w-52 bg-white border border-neutral-200 rounded-xl shadow-xl py-1" data-testid="todo-task-menu-panel">
            {[
              ["open", "Ouvrir / modifier", null],
              ["schedule", "Programmer (Agenda)", null],
              ["timer", menu.task.timer_started_at ? "Arrêter le chrono" : "Démarrer le chrono", null],
              ["source", menu.task.source_label ? `Élément lié : ${menu.task.source_label.slice(0, 26)}` : "Aucun élément lié", !menu.task.source_url],
              ["done", "Marquer comme finalisée", null],
              ["delete", "Supprimer", null],
            ].map(([id, label, disabled]) => (
              <button key={id} onClick={() => menuAction(id)} disabled={disabled} data-testid={`todo-menu-${id}`}
                className={`w-full text-left px-3 py-2 text-xs transition-colors ${disabled ? "text-neutral-300" : id === "delete" ? "text-rose-500 hover:bg-rose-50" : "text-neutral-600 hover:bg-neutral-50"}`}>
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Dossier finalisées */}
      <Dialog open={showDone} onOpenChange={setShowDone}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="todo-done-modal" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Archive className="w-4 h-4 text-violet-500" /> Dossier finalisées ({done.length})</DialogTitle></DialogHeader>
          <p className="text-[11px] text-neutral-400 -mt-1">Les tâches finalisées sont archivées et consultables dans ce dossier (filtres actifs de la page appliqués).</p>
          {done.length === 0 && <p className="py-8 text-center text-[12px] text-neutral-400">Aucune tâche finalisée pour ces filtres.</p>}
          {done.map((t) => (
            <div key={t.id} className="flex items-center gap-2.5 py-2 border-b border-neutral-50 last:border-0" data-testid={`todo-done-${t.id}`}>
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-[#0F172A] truncate">{t.title}</p>
                <p className="text-[10px] text-neutral-400 truncate">
                  {clientById[t.client_id]?.name || "Interne"}{t.service ? ` · ${t.service}` : ""}{t.completed_at ? ` · finalisée le ${frDate(t.completed_at.slice(0, 10))}` : ""}
                  {t.time_spent_sec ? ` · ${fmtSec(t.time_spent_sec)} passé(es)` : ""}
                </p>
              </div>
              {t.source_url && (
                <button onClick={() => navigate(t.source_url)} className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100" title="Ouvrir l'élément lié" data-testid={`todo-done-source-${t.id}`}>
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              )}
              <button onClick={() => patch(t, { status: "a_faire", archived: false }, "Tâche restaurée")} data-testid={`todo-done-restore-${t.id}`}
                className="text-[10px] font-semibold text-[#1D4ED8] hover:underline shrink-0">Restaurer</button>
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <TaskModal open={modal.open} task={modal.task} onClose={() => setModal({ open: false, task: null })}
        onSaved={load} clients={clients} users={users} />
      <ScheduleModal task={schedTask} onClose={() => setSchedTask(null)} onSaved={load} />
    </div>
  );
}
